import { useState, useRef, useEffect, useCallback } from 'react';
import type { Member } from '../../api/client';
import MentionPicker from './MentionPicker';
import ReactionPicker from './ReactionPicker';

interface SelectionComposerProps {
  visible: boolean;
  position: { x: number; y: number };
  selectedText: string;
  onSubmit: (body: string, mentions: Array<{ id: string; name: string }>) => void;
  onCancel: () => void;
  members?: Member[];
}

export default function SelectionComposer({
  visible,
  position,
  selectedText,
  onSubmit,
  onCancel,
  members = [],
}: SelectionComposerProps) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mentions, setMentions] = useState<Array<{ id: string; name: string }>>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus the input when visible
  useEffect(() => {
    if (visible) {
      setBody('');
      setMentions([]);
      setMentionQuery(null);
      setReactionPickerOpen(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [visible]);

  // Close on click outside
  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest?.('[data-reaction-picker]')
      ) {
        onCancel();
      }
    };
    // Delay adding the listener so the opening click doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onCancel]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mentionQuery !== null) {
          setMentionQuery(null);
        } else {
          onCancel();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, onCancel, mentionQuery]);

  // Detect @mention trigger in textarea
  const handleBodyChange = useCallback((value: string) => {
    setBody(value);

    const textarea = inputRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Look for an @ that starts a mention (after whitespace or at start of input)
    const atMatch = textBeforeCursor.match(/(?:^|[\s])@([^\s]*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const handleMentionSelect = useCallback((member: Member) => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = body.slice(0, cursorPos);

    // Find the @ that triggered the mention
    const atMatch = textBeforeCursor.match(/(?:^|[\s])@([^\s]*)$/);
    if (!atMatch) return;

    const atIndex = textBeforeCursor.lastIndexOf('@' + atMatch[1]);
    const beforeAt = body.slice(0, atIndex);
    const afterCursor = body.slice(cursorPos);
    const mentionText = `@${member.user.name} `;
    const newBody = beforeAt + mentionText + afterCursor;

    setBody(newBody);
    setMentionQuery(null);

    // Add to mentions if not already included
    if (!mentions.some((m) => m.id === member.user.id)) {
      setMentions((prev) => [...prev, { id: member.user.id, name: member.user.name }]);
    }

    // Restore focus and cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = atIndex + mentionText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [body, mentions]);

  const handleRemoveMention = useCallback((userId: string) => {
    setMentions((prev) => prev.filter((m) => m.id !== userId));
  }, []);

  const handleEmojiSelect = useCallback((emoji: string) => {
    const textarea = inputRef.current;
    if (!textarea) {
      setBody((prev) => `${prev}${emoji}`);
      return;
    }

    const selectionStart = textarea.selectionStart ?? body.length;
    const selectionEnd = textarea.selectionEnd ?? body.length;
    const nextBody = `${body.slice(0, selectionStart)}${emoji}${body.slice(selectionEnd)}`;
    setBody(nextBody);
    setReactionPickerOpen(false);

    setTimeout(() => {
      textarea.focus();
      const cursor = selectionStart + emoji.length;
      textarea.setSelectionRange(cursor, cursor);
    }, 0);
  }, [body]);

  if (!visible) return null;

  const truncatedText =
    selectedText.length > 50
      ? selectedText.slice(0, 50) + '\u2026'
      : selectedText;

  const handleSubmit = async () => {
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    try {
      onSubmit(body.trim(), mentions);
      setBody('');
      setMentions([]);
      setMentionQuery(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute z-50"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translateX(-50%) translateY(8px)',
        pointerEvents: 'auto',
      }}
    >
      <div
        className="w-[320px] rounded-[10px] overflow-visible"
        style={{
          background: 'var(--rd-surface-panel)',
          border: '1px solid var(--rd-border)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}
      >
        {/* Selected text preview */}
        <div
          className="px-[var(--sp-4)] py-[var(--sp-3)]"
          style={{
            background: 'var(--rd-surface-hover)',
            borderLeft: '2px solid var(--rd-text-faint)',
            borderRadius: '10px 10px 0 0',
          }}
        >
          <p
            className="text-[13px] italic m-0 truncate"
            style={{ color: 'var(--rd-avatar-default)' }}
          >
            &ldquo;{truncatedText}&rdquo;
          </p>
        </div>

        {/* Comment input */}
        <div className="p-[var(--sp-4)]">
          <div className="relative">
            <textarea
              ref={inputRef}
              value={body}
              onChange={(e) => handleBodyChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Add a comment... (@ to mention)"
              rows={3}
              className="w-full min-h-[72px] max-h-[140px] px-[var(--sp-3)] py-[var(--sp-3)] rounded-[var(--radius-md)] text-[13px] border outline-none transition-colors duration-[120ms] resize-none box-border"
              style={{
                background: 'var(--rd-surface-panel)',
                borderColor: 'var(--rd-border)',
                color: 'var(--rd-text-strong)',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--rd-text-placeholder)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--rd-border)')}
            />

            {/* Mention picker dropdown */}
            {mentionQuery !== null && members.length > 0 && (
              <MentionPicker
                members={members}
                query={mentionQuery}
                onSelect={handleMentionSelect}
                onClose={() => setMentionQuery(null)}
              />
            )}
          </div>

          {/* Mention chips */}
          {mentions.length > 0 && (
            <div className="flex flex-wrap gap-[var(--sp-1)] mt-[var(--sp-2)]">
              {mentions.map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-[var(--sp-1)] h-[22px] px-[var(--sp-2)] rounded-full text-[13px] font-semibold"
                  style={{
                    background: 'var(--rd-surface-highlight)',
                    color: 'var(--rd-accent)',
                  }}
                >
                  @{m.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveMention(m.id)}
                    className="flex items-center justify-center w-[14px] h-[14px] rounded-full border-0 cursor-pointer p-0"
                    style={{
                      background: 'transparent',
                      color: 'var(--rd-accent)',
                    }}
                    aria-label={`Remove mention of ${m.name}`}
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M1 1l6 6M7 1l-6 6" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-[var(--sp-2)] mt-[var(--sp-3)]">
            <div className="relative">
              <button
                type="button"
                onClick={() => setReactionPickerOpen((open) => !open)}
                className="w-[28px] h-[28px] flex items-center justify-center rounded-[7px] cursor-pointer border transition-colors duration-[120ms]"
                style={{
                  background: 'transparent',
                  borderColor: 'var(--rd-border)',
                  color: 'var(--rd-text-faint)',
                }}
                aria-label="Insert emoji"
                title="Insert emoji"
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="5.75" cy="6.25" r="0.9" fill="currentColor" />
                  <circle cx="10.25" cy="6.25" r="0.9" fill="currentColor" />
                  <path
                    d="M5.2 9.6c.7.9 1.67 1.35 2.8 1.35 1.13 0 2.1-.45 2.8-1.35"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              {reactionPickerOpen && (
                <ReactionPicker
                  onSelect={handleEmojiSelect}
                  onClose={() => setReactionPickerOpen(false)}
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-[var(--sp-2)]">
            <button
              type="button"
              onClick={onCancel}
              className="h-[28px] px-[var(--sp-3)] rounded-[7px] text-[13px] font-semibold cursor-pointer border-0 transition-colors duration-[120ms]"
              style={{
                background: 'transparent',
                color: 'var(--rd-text-subtle)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!body.trim() || submitting}
              className="h-[28px] px-[var(--sp-4)] rounded-[7px] text-[13px] font-semibold cursor-pointer border-0 transition-colors duration-[120ms] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: 'var(--rd-accent)',
                color: '#ffffff',
              }}
            >
              Comment
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
